import {
	index,
	integer,
	sqliteTable,
	text,
	uniqueIndex,
} from "drizzle-orm/sqlite-core";

export const tenantConfig = sqliteTable("tenant_config", {
	key: text("key").primaryKey(),
	value: text("value"),
});

export const user = sqliteTable("user", {
	id: text("id").primaryKey(),
	name: text("name").notNull(),
	email: text("email").notNull().unique(),
	emailVerified: integer("email_verified", { mode: "boolean" })
		.notNull()
		.default(false),
	image: text("image"),
	role: text("role").default("member"),
	banned: integer("banned", { mode: "boolean" }).default(false),
	banReason: text("ban_reason"),
	banExpires: integer("ban_expires", { mode: "timestamp" }),
	tosVersionAccepted: text("tos_version_accepted"),
	tosAcceptedAt: integer("tos_accepted_at", { mode: "timestamp" }),
	lastDataExportAt: integer("last_data_export_at", { mode: "timestamp" }),
	changelogLastViewedAt: integer("changelog_last_viewed_at", {
		mode: "timestamp",
	}),
	platformUserId: text("platform_user_id"),
	deletedAt: integer("deleted_at", { mode: "timestamp" }),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
});

export const auditLog = sqliteTable("audit_log", {
	id: text("id").primaryKey(),
	userId: text("user_id").notNull(),
	userEmail: text("user_email").notNull(),
	action: text("action").notNull(),
	metadata: text("metadata"),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
});

export const session = sqliteTable("session", {
	id: text("id").primaryKey(),
	expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
	token: text("token").notNull().unique(),
	ipAddress: text("ip_address"),
	userAgent: text("user_agent"),
	userId: text("user_id")
		.notNull()
		.references(() => user.id, { onDelete: "cascade" }),
	impersonatedBy: text("impersonated_by"),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
});

export const account = sqliteTable("account", {
	id: text("id").primaryKey(),
	accountId: text("account_id").notNull(),
	providerId: text("provider_id").notNull(),
	userId: text("user_id")
		.notNull()
		.references(() => user.id, { onDelete: "cascade" }),
	accessToken: text("access_token"),
	refreshToken: text("refresh_token"),
	idToken: text("id_token"),
	accessTokenExpiresAt: integer("access_token_expires_at", {
		mode: "timestamp",
	}),
	refreshTokenExpiresAt: integer("refresh_token_expires_at", {
		mode: "timestamp",
	}),
	scope: text("scope"),
	password: text("password"),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
});

export const notification = sqliteTable(
	"notification",
	{
		id: text("id").primaryKey(),
		userId: text("user_id")
			.notNull()
			.references(() => user.id, { onDelete: "cascade" }),
		type: text("type").notNull(),
		title: text("title").notNull(),
		body: text("body").notNull(),
		metadata: text("metadata"),
		read: integer("read", { mode: "boolean" }).notNull().default(false),
		createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	},
	(table) => [
		index("idx_notification_user_read_created").on(
			table.userId,
			table.read,
			table.createdAt,
		),
	],
);

export const invitation = sqliteTable(
	"invitation",
	{
		id: text("id").primaryKey(),
		email: text("email").notNull(),
		role: text("role").notNull().default("member"),
		invitedBy: text("invited_by")
			.notNull()
			.references(() => user.id, { onDelete: "cascade" }),
		token: text("token").notNull().unique(),
		status: text("status").notNull().default("pending"),
		expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
		createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	},
	(table) => [
		index("idx_invitation_email_status").on(table.email, table.status),
	],
);

export const webhookEndpoints = sqliteTable(
	"webhook_endpoints",
	{
		id: text("id").primaryKey(),
		url: text("url").notNull(),
		events: text("events").notNull(),
		signingSecret: text("signing_secret").notNull(),
		active: integer("active", { mode: "boolean" }).notNull().default(true),
		metadata: text("metadata"),
		maxRetries: integer("max_retries"),
		timeoutMs: integer("timeout_ms"),
		createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
		updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
	},
	(table) => [index("idx_webhook_endpoints_active").on(table.active)],
);

export const webhookDeliveries = sqliteTable(
	"webhook_deliveries",
	{
		id: text("id").primaryKey(),
		endpointId: text("endpoint_id")
			.notNull()
			.references(() => webhookEndpoints.id, { onDelete: "cascade" }),
		eventType: text("event_type").notNull(),
		payload: text("payload").notNull(),
		statusCode: integer("status_code"),
		success: integer("success", { mode: "boolean" }),
		responseBody: text("response_body"),
		attempts: integer("attempts").notNull().default(0),
		lastAttemptAt: integer("last_attempt_at", { mode: "timestamp" }),
		nextRetryAt: integer("next_retry_at", { mode: "timestamp" }),
		createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	},
	(table) => [
		index("idx_webhook_deliveries_endpoint_created").on(
			table.endpointId,
			table.createdAt,
		),
	],
);

export const webhookDeliveryAttempts = sqliteTable(
	"webhook_delivery_attempts",
	{
		id: text("id").primaryKey(),
		deliveryId: text("delivery_id")
			.notNull()
			.references(() => webhookDeliveries.id, { onDelete: "cascade" }),
		attemptNumber: integer("attempt_number").notNull(),
		statusCode: integer("status_code"),
		responseBody: text("response_body"),
		latencyMs: integer("latency_ms"),
		createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	},
	(table) => [
		index("idx_webhook_delivery_attempts_delivery_attempt").on(
			table.deliveryId,
			table.attemptNumber,
		),
	],
);

export const webhookDlq = sqliteTable(
	"webhook_dlq",
	{
		id: text("id").primaryKey(),
		endpointId: text("endpoint_id")
			.notNull()
			.references(() => webhookEndpoints.id, { onDelete: "cascade" }),
		eventType: text("event_type").notNull(),
		encryptedPayload: text("encrypted_payload").notNull(),
		error: text("error"),
		attemptCount: integer("attempt_count").notNull(),
		createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
		retriedAt: integer("retried_at", { mode: "timestamp" }),
	},
	(table) => [
		index("idx_webhook_dlq_endpoint_created").on(
			table.endpointId,
			table.createdAt,
		),
	],
);

export const notificationPreferences = sqliteTable(
	"notification_preferences",
	{
		id: text("id").primaryKey(),
		userId: text("user_id")
			.notNull()
			.references(() => user.id, { onDelete: "cascade" }),
		marketing: integer("marketing", { mode: "boolean" })
			.notNull()
			.default(true),
		productUpdates: integer("product_updates", { mode: "boolean" })
			.notNull()
			.default(true),
		securityAlerts: integer("security_alerts", { mode: "boolean" })
			.notNull()
			.default(true),
		createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
		updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
	},
	(table) => [
		uniqueIndex("idx_notification_preferences_user").on(table.userId),
	],
);

export const deletionRequest = sqliteTable("deletion_request", {
	id: text("id").primaryKey(),
	userId: text("user_id")
		.notNull()
		.references(() => user.id),
	token: text("token").notNull().unique(),
	expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
	status: text("status").default("pending"),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
});

export const events = sqliteTable("events", {
	id: integer("id").primaryKey({ autoIncrement: true }),
	type: text("type").notNull(),
	payload: text("payload").notNull(),
	targetUserId: text("target_user_id"),
	createdAt: integer("created_at").notNull(),
});

export const pushDeviceTokens = sqliteTable("push_device_tokens", {
	id: text("id").primaryKey(),
	userId: text("user_id")
		.notNull()
		.references(() => user.id, { onDelete: "cascade" }),
	token: text("token").notNull().unique(),
	platform: text("platform").notNull(),
	createdAt: integer("created_at").notNull(),
	updatedAt: integer("updated_at").notNull(),
});

export const files = sqliteTable("files", {
	id: text("id").primaryKey(),
	name: text("name").notNull(),
	contentType: text("content_type").notNull(),
	sizeBytes: integer("size_bytes").notNull(),
	r2Key: text("r2_key").notNull(),
	uploadedBy: text("uploaded_by")
		.notNull()
		.references(() => user.id, { onDelete: "cascade" }),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
});

export const project = sqliteTable("project", {
	id: text("id").primaryKey(),
	name: text("name").notNull(),
	description: text("description"),
	status: text("status").default("active"),
	createdBy: text("created_by")
		.notNull()
		.references(() => user.id, { onDelete: "cascade" }),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
	version: integer("version").notNull().default(1),
});

export const apiKeys = sqliteTable(
	"api_keys",
	{
		id: text("id").primaryKey(),
		keyHash: text("key_hash").notNull(),
		keyPrefix: text("key_prefix").notNull(),
		name: text("name").notNull(),
		scopes: text("scopes").notNull(),
		createdBy: text("created_by")
			.notNull()
			.references(() => user.id, { onDelete: "cascade" }),
		lastUsedAt: integer("last_used_at", { mode: "timestamp" }),
		expiresAt: integer("expires_at", { mode: "timestamp" }),
		revokedAt: integer("revoked_at", { mode: "timestamp" }),
		createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
		updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
	},
	(table) => [
		uniqueIndex("idx_api_keys_key_hash").on(table.keyHash),
		index("idx_api_keys_created_by").on(table.createdBy),
	],
);

export const verification = sqliteTable("verification", {
	id: text("id").primaryKey(),
	identifier: text("identifier").notNull(),
	value: text("value").notNull(),
	expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
	createdAt: integer("created_at", { mode: "timestamp" }),
	updatedAt: integer("updated_at", { mode: "timestamp" }),
});
